import type { GuidePage } from '../guides';

const example = [
  { item: 'Team time: 2 cleaners x 3 hours for a first deep clean (3 bed / 2 bath), at loaded hourly cost', amount: '$168' },
  { item: 'Supplies and equipment wear for the visit', amount: '$14' },
  { item: 'Drive time, 25 minutes each way, plus mileage', amount: '$22' },
  { item: 'Overhead share: insurance, booking and admin time, software, marketing', amount: '$40' },
  { item: 'Margin chosen for first-time deep cleans', amount: '$76' },
];

const faq = [
  {
    q: 'Should I charge cleaning by the hour, by the room or flat rate?',
    a: 'Customers usually prefer a flat price per clean, and many cleaning businesses quote by home size, bedrooms and bathrooms, or by room. Underneath, the price should still come from the hours your team needs. Hourly billing is simpler for you but makes customers watch the clock.',
  },
  {
    q: 'Why is a first clean priced higher than a regular one?',
    a: 'Because it takes longer. Built-up grime, baseboards and inside fixtures that have not been touched in months add time. Price the first visit on its own and the maintenance visits after it separately.',
  },
  {
    q: 'How do I price move-out cleans?',
    a: 'As a deep clean of an empty home, plus the inside of cabinets, appliances and closets. Ask whether utilities will be on and whether a landlord checklist applies, because both change the time.',
  },
  {
    q: 'Should pets change the price?',
    a: 'Heavy shedding adds vacuuming and lint removal in every room, so many companies ask about pets on every quote and adjust for them.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-house-cleaning',
  path: '/resources/how-to-price-house-cleaning',
  primaryKeyword: 'how to price house cleaning',
  secondaryKeywords: ['how much to charge for house cleaning', 'cleaning business pricing', 'deep clean pricing'],
  title: 'How to Price House Cleaning Jobs (With an Example) | Scopie', // 58
  metaDescription:
    'Price house cleaning from team hours, supplies, drive time, overhead and margin, with separate prices for first cleans, maintenance cleans and move-outs.', // 154
  h1: 'How to price house cleaning',
  answerFirst:
    'Price house cleaning from the time your team actually spends in the home, plus supplies, drive time, a share of overhead and your margin. Price a first deep clean separately from regular maintenance visits, and treat move-outs as their own job. The method and worked example below show how the numbers come together.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  schemaType: 'Article',
  blocks: [
    {
      type: 'text',
      id: 'time',
      h2: 'Start with time, not with a rate card',
      lead: '"What do you charge for a three-bedroom?"',
      paragraphs: [
        'Two three-bedroom homes can take an hour apart: one lived in by a tidy couple, the other by three kids and two dogs. A rate card by bedroom count is a convenient way to answer the phone, but the number on it should come from how long homes like that really take your team.',
        'Track a few weeks of jobs: home size, condition, type of clean and the hours spent. Those notes become your own pricing table, which beats any figure copied from another company.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'Worked example: a first deep clean',
      label: 'Building the price for one visit (internal costing)',
      lines: example,
      total: '$320',
      totalLabel: 'Price to quote',
      note: 'Illustrative numbers. Use your own wages, time and costs; markets differ a lot.',
    },
    {
      type: 'list',
      id: 'types',
      h2: 'Price each type of clean on its own',
      style: 'bullets',
      items: [
        { title: 'First or deep clean', text: 'The longest visit: baseboards, door frames, cabinet fronts, built-up kitchen and bath grime.' },
        { title: 'Maintenance clean', text: 'Shorter, because the home is kept up. The per-visit price is lower; repeat business makes it worthwhile.' },
        { title: 'Move-out or move-in', text: 'Empty home, but inside every cabinet, closet and appliance. Ask about utilities and landlord checklists.' },
        { title: 'Post-construction', text: 'Fine dust on every surface, stickers on windows, paint specks. Often needs more than one pass.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Pricing mistakes in residential cleaning',
      style: 'mistakes',
      items: [
        { title: 'Wage instead of loaded cost', text: 'Payroll taxes, workers compensation and paid travel make an hour cost more than the wage.' },
        { title: 'Free extras', text: 'Inside ovens, fridges and windows are real time; price them as add-ons.' },
        { title: 'Ignoring drive time', text: 'A spread-out schedule can eat an hour a day per team.' },
        { title: 'No lockout or late-cancellation charge', text: 'A wasted trip still costs wages and fuel.' },
      ],
    },
    {
      type: 'text',
      id: 'quote',
      h2: 'Turning the price into a quote',
      paragraphs: [
        'In Scopie you save your clean types and add-ons as items with your working prices, then build each quote in a couple of minutes. Extras like inside the oven can be optional lines the customer ticks. Your lockout and cancellation policy rides along as a saved clause, and the quote is emailed to the customer to accept.',
      ],
      links: [{ phrase: 'build each quote in a couple of minutes', href: '/for/cleaning' }],
    },
    { type: 'faq', id: 'faq', h2: 'House cleaning pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/templates/house-cleaning-quote-template', anchor: 'house cleaning quote template', status: 'live' },
    { href: '/resources', anchor: 'All pricing guides', status: 'live' },
  ],
  cta: {
    heading: 'Quote your next clean from your own numbers',
    text: 'Save your clean types and extras in Scopie and email each cleaning quote as a link.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
